import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api, downloadFile } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CalendarX2, CheckCircle2, FileDown, Loader2, MailQuestion } from "lucide-react";
import { RequestStatementButton } from "@/components/statements/StatementRequests";
import { BooksChecklist, BooksCompleteBadge, useBooksStatus } from "@/components/statements/BooksStatus";

const bc = { borderColor: "var(--ll-border)" };

export default function MissingMonths() {
  const { user } = useAuth();
  const canRequest = ["owner", "admin", "accountant"].includes(user?.role);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const books = useBooksStatus(tick);
  const load = useCallback(() => { setTick((t) => t + 1); return api.get("/statement-requests/missing-months").then((r) => setData(r.data))
    .catch(() => toast.error("Could not load missing months")); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <PageHeader title="Missing Months" subtitle="Gaps between uploaded statements, by account" testid="missing-months-header"
        actions={<ClosePackButton />} />
      <PageBody>
        {!data ? <div className="p-16 grid place-items-center"><Loader2 className="animate-spin" /></div> : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
              <Kpi label="Missing months" value={data.total_missing} testid="mm-kpi-missing" tone={data.total_missing ? "#DC2626" : "#059669"} />
              <Kpi label="Accounts with gaps" value={data.accounts.filter((a) => a.missing_count).length} testid="mm-kpi-accounts" />
              <Kpi label="Open requests" value={data.open_requests} testid="mm-kpi-requests" tone="#1D4ED8" />
              <Kpi label="Books complete" value={`${books.filter((b) => b.complete).length}/${books.filter((b) => b.has_statements).length}`} testid="mm-kpi-books" tone="#5B21B6" />
            </div>
            <div className="space-y-4">
              {data.accounts.map((a) => <AccountCard key={a.bank_account_id} a={a} canRequest={canRequest} reload={load}
                books={books.find((b) => b.bank_account_id === a.bank_account_id)} />)}
              {!data.accounts.length && <div className="text-sm text-[var(--ll-ink-soft)]" data-testid="mm-empty">No bank accounts yet.</div>}
            </div>
          </>
        )}
      </PageBody>
    </>
  );
}

function ClosePackButton() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear - 1);
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { await downloadFile(`/books/close-pack?year=${year}`, `year-end-close-pack-${year}.pdf`); }
    catch (err) {
      let msg = "No accounts have complete books through December of that year yet";
      try { msg = JSON.parse(await err?.response?.data?.text?.())?.detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2" data-testid="close-pack">
      <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="px-2 py-2 border bg-white text-sm" style={bc} data-testid="close-pack-year">
        {Array.from({ length: 6 }, (_, i) => thisYear - i).map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <button onClick={download} disabled={busy} data-testid="close-pack-download"
        className="text-sm px-3 py-2 text-white bg-[var(--ll-ink)] inline-flex items-center gap-1.5 disabled:opacity-50">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Year-End Close Pack
      </button>
    </div>
  );
}

function Kpi({ label, value, testid, tone = "var(--ll-ink)" }) {
  return (
    <div className="ll-card-flat p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">{label}</div>
      <div className="font-display font-bold text-3xl mt-1 font-mono-fin" style={{ color: tone }}>{value}</div>
    </div>
  );
}

function AccountCard({ a, canRequest, reload, books }) {
  const [busy, setBusy] = useState(false);
  const pending = a.gaps.filter((g) => !g.request);
  const requestAll = async () => {
    setBusy(true);
    let ok = 0;
    for (const g of pending) {
      try { await api.post("/statement-requests", { bank_account_id: a.bank_account_id, start: g.start, end: g.end }); ok += 1; }
      catch (err) { toast.error(err?.response?.data?.detail || `Couldn't request ${g.range_text}`); break; }
    }
    if (ok) toast.success(`Requested ${ok} missing period${ok === 1 ? "" : "s"} for ${a.label}`);
    setBusy(false);
    reload();
  };
  return (
    <div className="ll-card-flat overflow-hidden" data-testid={`mm-account-${a.bank_account_id}`}>
      <div className="px-5 py-3 border-b flex flex-wrap items-center justify-between gap-3" style={bc}>
        <div>
          <div className="font-display font-bold">{a.label}</div>
          <div className="text-xs text-[var(--ll-ink-soft)]">
            {a.statement_count ? `${a.statement_count} statements · ${fmtDate(a.first)} → ${fmtDate(a.last)}` : "No statements uploaded yet"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {books?.complete && <BooksCompleteBadge through={books.through_label} testid={`mm-books-complete-${a.bank_account_id}`} />}
          {a.missing_count ? (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1" style={{ background: "#FEF2F2", color: "#DC2626" }} data-testid={`mm-count-${a.bank_account_id}`}>
              {a.missing_count} missing month{a.missing_count === 1 ? "" : "s"}
            </span>
          ) : a.statement_count ? (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 inline-flex items-center gap-1" style={{ background: "#ECFDF5", color: "#059669" }} data-testid={`mm-complete-${a.bank_account_id}`}>
              <CheckCircle2 size={11} /> No gaps
            </span>
          ) : null}
          {canRequest && pending.length > 0 && (
            <button onClick={requestAll} disabled={busy} data-testid={`mm-request-all-${a.bank_account_id}`}
              className="text-xs px-3 py-1.5 text-white bg-[var(--ll-ink)] inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <MailQuestion size={12} />} Request all {pending.length}
            </button>
          )}
        </div>
      </div>
      {a.gaps.length > 0 && (
        <ul className="divide-y" style={bc}>
          {a.gaps.map((g) => (
            <li key={g.start} className="px-5 py-2.5 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`mm-gap-${a.bank_account_id}-${g.start}`}>
              <span className="inline-flex items-center gap-2">
                <CalendarX2 size={14} className="text-[#DC2626]" />
                <b>{g.range_text}</b>
                <span className="text-xs text-[var(--ll-ink-muted)]">between {g.after_file} and {g.before_file}</span>
              </span>
              <span className="inline-flex items-center gap-2">
                {canRequest && <RequestStatementButton accountId={a.bank_account_id} start={g.start} end={g.end} request={g.request}
                  onDone={reload} testid={`mm-request-${a.bank_account_id}-${g.start}`} />}
                <Link to={`/app/statements`} className="text-xs hover:underline" data-testid={`mm-open-${a.bank_account_id}-${g.start}`}>Statements</Link>
              </span>
            </li>
          ))}
        </ul>
      )}
      <BooksChecklist status={books} />
    </div>
  );
}
